import { Button, Input } from '30pay/components';
import { useEffect, useState } from 'react';
import {
    cardNumberFormatter,
    copyToClipboard,
    detectBank,
    detectBankByName,
    formatCardNumber,
    objectToFormData,
    plainNumber,
    postFormData,
    priceDigit,
} from 'scripts';
import { API } from 'data/30pay.api';
import { TbCopy } from 'react-icons/tb';
import { __ConvertBoxCoinsListRef } from '30pay/trade/convertBox/initial/coinsList/coinsList.interface';
import { __ConvertBoxConfirmOrderDetailModalRef } from '30pay/trade/convertBox/confirmOrderModal/orderDetailModal.interface';
import { BankAccount, OfflineDepositSettings } from 'interfaces';
import { Spin } from 'antd';
import { BsCamera, BsChevronDown, BsFillInfoSquareFill, BsImage } from 'react-icons/bs';
import CameraCaptureModal from './cameraModal/cameraModal.index';
import { CardNumberSelectionModal } from '../../cardNumberSelectionModal/cardNumberSelectionModal.index';
import { CoinIcon } from 'utilities/components';
import { getCardNumbers, getOfflineDepositSettings } from 'services';
import { toast } from 'react-toastify';
import { OfflineDepositSelectTargetCardModal } from './selectTargetBankModal/selectTargetBankModal.index';

const BuyPaymentOfflineMethod: React.FC<{ onFinish: () => void }> = (props: { onFinish: () => void }) => {
    const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState<File | null>(null); // State for the captured image
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [cardNumbers, setCardNumbers] = useState<Omit<BankAccount, 'iBan'>[]>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [cardNumbersLoading, setCardNumbersLoading] = useState(true);
    const [selectedCardNumber, setSelectedCardNumber] = useState<Omit<BankAccount, 'iBan'>>();
    const [amount, setAmount] = useState<number>();
    const [offlineDepositSettingsLoading, setOfflineDepositSettingsLoading] = useState<boolean>(false);
    const [offlineDepositSettings, setOfflineDepositSettings] = useState<OfflineDepositSettings[]>();
    const [selectedOfflineDepositSetting, setSelectedOfflineDepositSetting] = useState<OfflineDepositSettings>();
    const [selectTargetCardModalOpen, setSelectTargetCardModalOpen] = useState(false);

    const handleCapture = (image: File) => {
        setSelectedImage(null);
        setCapturedImage(image); // Set the captured image
        setIsCameraModalOpen(false); // Close the modal
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setCapturedImage(null); // Clear captured image
            setSelectedImage(file); // Set the selected image as a File object
        }
    };

    useEffect(() => {
        if (offlineDepositSettings && offlineDepositSettings.length !== 0)
            setSelectedOfflineDepositSetting(offlineDepositSettings[0]);
    }, [offlineDepositSettings]);

    function submitHandler() {
        const receiptImage = capturedImage ?? selectedImage;
        const cardNumberId = selectedCardNumber?.id;

        if (!receiptImage || !amount || !cardNumberId) {
            toast.error('لطفاً مبلغ، شماره کارت و تصویر فیش را وارد نمایید.');
            return;
        }

        setLoading(true);
        const body = {
            receiptImage,
            amount,
            cardNumberId,
            platform: 1,
        };
        postFormData(API.transaction.deposit, objectToFormData(body))
            .then((res) => {
                toast.success(res.value.message);
                props.onFinish();
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    }

    const onCardNumberSelection = (cardNumber: Omit<BankAccount, 'iBan'>) => {
        setSelectedCardNumber(cardNumber);
        setModalOpen(false);
    };

    useEffect(() => {
        getCardNumbers(setCardNumbersLoading, setCardNumbers, setSelectedCardNumber);
    }, []);

    const onGetOfflineDepositSettings = () => {
        getOfflineDepositSettings(
            setOfflineDepositSettingsLoading,
            {
                cardNumberId: selectedCardNumber?.id,
                amount: amount,
            },
            setOfflineDepositSettings,
        );
    };

    return (
        <div className="flex flex-col w-full h-full">
            <OfflineDepositSelectTargetCardModal
                isOpen={selectTargetCardModalOpen}
                onClose={() => setSelectTargetCardModalOpen(false)}
                onSelect={(e) => setSelectedOfflineDepositSetting(e)}
                data={offlineDepositSettings}
            />
            <CameraCaptureModal
                isOpen={isCameraModalOpen}
                onClose={() => setIsCameraModalOpen(false)}
                onCapture={handleCapture}
            />
            {cardNumbers && (
                <CardNumberSelectionModal
                    open={modalOpen}
                    setOpen={setModalOpen}
                    cardNumbers={cardNumbers}
                    onSelect={onCardNumberSelection}
                />
            )}
            {!offlineDepositSettings && (
                <>
                    <label className={'max-lg:text-sm'}>شماره کارت:</label>
                    <Spin spinning={cardNumbersLoading}>
                        <div
                            onClick={() => setModalOpen(true)}
                            className={
                                'flex justify-between items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 max-lg:h-12 max-lg:text-sm mt-2 clickable'
                            }
                        >
                            {selectedCardNumber && (
                                <>
                                    <div className={'flex items-center gap-4'}>
                                        <img
                                            src={detectBank(selectedCardNumber?.cardNumber)?.bank_logo}
                                            className={'w-6 h-6'}
                                        />
                                        {selectedCardNumber?.bank?.name}
                                    </div>
                                    {formatCardNumber(selectedCardNumber?.cardNumber)}
                                    <BsChevronDown />
                                </>
                            )}
                        </div>
                    </Spin>
                    <label className={'mt-4 max-lg:text-sm'}>مبلغ واریزی‌ (تومان):</label>
                    <Input
                        placeholder={'مبلغ واریزی را وارد نمایید...'}
                        className={'mt-2'}
                        inputMode={'numeric'}
                        type={'text'}
                        value={priceDigit(amount, '،')}
                        onChange={(e) => setAmount(Number(plainNumber(e.target.value.replace(/[^۰-۹0-9,.]/g, ''))))}
                        suffix={<CoinIcon symbol={'IRT'} className={'mr-2'} />}
                        dir={amount || amount === 0 ? 'ltr' : 'rtl'}
                    />
                    <Button
                        className={'mt-6'}
                        loading={offlineDepositSettingsLoading}
                        type={'primary'}
                        disabled={offlineDepositSettingsLoading || !amount || !selectedCardNumber}
                        onClick={onGetOfflineDepositSettings}
                    >
                        دریافت اطلاعات واریز
                    </Button>
                </>
            )}
            {selectedOfflineDepositSetting && (
                <>
                    {offlineDepositSettings !== null ? (
                        <div className="flex flex-col gap-4">
                            <div
                                onClick={() => setSelectTargetCardModalOpen(true)}
                                className={
                                    'flex gap-4 items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 max-lg:h-12 max-lg:text-sm mt-2 clickable'
                                }
                            >
                                <div className={'flex items-center gap-4'}>
                                    <img
                                        src={detectBankByName(selectedOfflineDepositSetting?.bankName)?.bank_logo}
                                        className={'w-6 h-6'}
                                    />
                                </div>
                                <p className="text-base font-bold text-darkPurple-100 flex-1">
                                    حساب بانکی سی پی - {selectedOfflineDepositSetting.bankName}
                                </p>
                                <BsChevronDown />
                            </div>
                            <div className="bg-[#D2DEFE33] p-6 gap-3 flex flex-col rounded-lg">
                                <h6 className="font-bold text-base text-darkPurple-100 w-full">اطلاعات مقصد</h6>
                                <div className="flex flex-col gap-2 w-full">
                                    <div className="w-full flex items-center">
                                        <label className="font-bold text-sm text-darkPurple-100">شماره شبا مقصد</label>
                                        <span className="flex-1" />
                                        {selectedOfflineDepositSetting && (
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-base text-darkPurple-100 en">
                                                    {selectedOfflineDepositSetting.iBan}
                                                </p>
                                                <TbCopy
                                                    className={'clickable'}
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            `${selectedOfflineDepositSetting.iBan}`,
                                                            'شماره شبا',
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex items-center">
                                        <label className="font-bold text-sm text-darkPurple-100">شماره حساب مقصد</label>
                                        <span className="flex-1" />
                                        {selectedOfflineDepositSetting && (
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-base text-darkPurple-100">
                                                    {selectedOfflineDepositSetting.bankAccountNumber}
                                                </p>
                                                <TbCopy
                                                    className={'clickable'}
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            selectedOfflineDepositSetting.bankAccountNumber,
                                                            'شماره حساب',
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex items-center">
                                        <label className="font-bold text-sm text-darkPurple-100">شماره کارت مقصد</label>
                                        <span className="flex-1" />
                                        {selectedOfflineDepositSetting && (
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-base text-darkPurple-100">
                                                    {cardNumberFormatter(selectedOfflineDepositSetting.cardNumber)}
                                                </p>
                                                <TbCopy
                                                    className={'clickable'}
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            selectedOfflineDepositSetting.cardNumber,
                                                            'شماره کارت',
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full flex items-center">
                                        <label className="font-bold text-sm text-darkPurple-100">نام بانک</label>
                                        <span className="flex-1" />
                                        {selectedOfflineDepositSetting && (
                                            <p className="font-bold text-base text-darkPurple-100">
                                                {selectedOfflineDepositSetting.bankName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-full flex items-center">
                                        <label className="font-bold text-sm text-darkPurple-100">
                                            نام صاحب کارت مقصد
                                        </label>
                                        <span className="flex-1" />
                                        {selectedOfflineDepositSetting && (
                                            <p className="font-bold text-base text-darkPurple-100">
                                                {selectedOfflineDepositSetting.ownerName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p className="mx-auto text-darkPurple-60 text-base font-medium">
                            اطلاعات حساب مقصد موجود نیست !
                        </p>
                    )}

                    <div className="flex flex-col mt-6 gap-4">
                        <h6 className="text-base font-bold text-darkPurple-100">افزودن تصویر فیش واریزی:</h6>
                        <div className="flex items-center gap-4 max-h-[86px] h-[86px]">
                            <div
                                onClick={() => setIsCameraModalOpen(true)}
                                className="flex-1 h-full w-full bg-white border border-primary-20 rounded-lg flex flex-col gap-3 items-center justify-center text-primary-100 clickable"
                            >
                                {capturedImage ? (
                                    <div className="rounded-lg overflow-hidden m-auto w-fit h-[80%]">
                                        <img
                                            src={URL.createObjectURL(capturedImage)}
                                            alt="Captured"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <BsCamera size={24} />
                                        <p className="text-sm font-medium">عکس برداری</p>
                                    </>
                                )}
                            </div>

                            <div
                                className={`flex-1 h-full w-full bg-white border border-primary-20 rounded-lg flex flex-col gap-3 items-center justify-center text-primary-100 clickable`}
                                onClick={() => document.getElementById('file-input')?.click()} // Trigger file input click
                            >
                                {selectedImage ? (
                                    <div className="rounded-lg overflow-hidden m-auto w-fit h-[80%]">
                                        <img
                                            src={URL.createObjectURL(selectedImage)}
                                            alt="Selected"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <BsImage size={24} />
                                        <p className="text-sm font-medium">انتخاب فایل</p>
                                    </>
                                )}
                            </div>
                            <input
                                id="file-input"
                                type="file"
                                accept=".jpg,.jpeg,.png,.heic"
                                style={{ display: 'none' }} // Hide the input
                                onChange={handleFileChange}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <BsFillInfoSquareFill size={16} color="#F6A609" />
                            <p className="font-medium text-sm text-black-100">
                                حجم تصویر باید کمتر از ۵ مگابایت و در فرمت PNG ،JPG و یا HEIC باشد.
                            </p>
                        </div>
                    </div>
                    <Button
                        loading={loading}
                        onClick={submitHandler}
                        className="w-full h-16 mt-6"
                        type="primary"
                        disabled={loading || !(capturedImage ?? selectedImage) || !amount || !selectedCardNumber?.id}
                    >
                        ثبت درخواست
                    </Button>
                </>
            )}
        </div>
    );
};

export default BuyPaymentOfflineMethod;
